import { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Trash2,
  Edit3,
  Check,
  X,
  Calendar,
  Building,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { merchantsApi, usersApi } from "../../api/client";
import Button from "../UI/Button";
import Badge from "../UI/Badge";
import Modal from "../UI/Modal";
import toast from "react-hot-toast";

// Helper functions moved to module scope to keep stable references
const toIsoDate = (date) => {
  const d = new Date(date);
  // Normalize to local midnight before ISO, to avoid TZ drift
  d.setHours(0, 0, 0, 0);
  return new Date(d).toISOString().split("T")[0];
};

const generateDateColumns = (startIso, endIso) => {
  const dates = [];
  const start = new Date(startIso);
  const end = new Date(endIso);
  // Ensure start <= end
  if (start > end) return dates;

  const cur = new Date(start);
  while (cur <= end) {
    const key = toIsoDate(cur);
    dates.push({
      key,
      label: cur.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      fullDate: new Date(cur),
    });
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};

const MerchantSpreadsheet = ({ userRole = "staff" }) => {
  const [merchants, setMerchants] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMerchants, setTotalMerchants] = useState(0);
  // CSV Download logic
  const downloadCSV = (all = false) => {
    // Prepare columns
    const columns = ["S/N", "Username", "Business Name", "Status"];
    let dateKeys = dateColumns.map((d) => d.key);
    if (all) {
      // Find all unique transaction dates across all merchants
      const allDates = new Set();
      merchants.forEach((m) => {
        Object.keys(m.transactions || {}).forEach((date) => allDates.add(date));
      });
      dateKeys = Array.from(allDates).sort();
    }
    columns.push(...dateKeys);
    columns.push("Remarks");
    if (userRole === "admin") columns.push("Assigned To");
    // Prepare rows
    const rows = merchants.map((m, idx) => {
      const row = [idx + 1, m.username, m.businessName, m.status];
      dateKeys.forEach((date) => {
        row.push(m.transactions?.[date]?.count || 0);
      });
      row.push(m.remarks || "");
      if (userRole === "admin") row.push(m.assignedTo || "");
      return row;
    });
    // CSV string
    let csv = columns.join(",") + "\n";
    rows.forEach((row) => {
      csv +=
        row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",") + "\n";
    });
    // Download
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = all
      ? "all_transactions.csv"
      : "transactions_by_date_range.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [merchantToDelete, setMerchantToDelete] = useState(null);
  const [setPendingChanges] = useState(new Map());
  const [scrollPosition, setScrollPosition] = useState(0);

  const scrollContainerRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const editInputRef = useRef(null);

  // Date range state (default: last 30 days)

  const today = new Date();
  const defaultStart = new Date(today);
  defaultStart.setDate(defaultStart.getDate() - 29);

  const [startDate, setStartDate] = useState(toIsoDate(defaultStart));
  const [endDate, setEndDate] = useState(toIsoDate(today));

  const dateColumns = useMemo(
    () => generateDateColumns(startDate, endDate),
    [startDate, endDate]
  );

  // New merchant form
  const [newMerchant, setNewMerchant] = useState({
    username: "",
    businessName: "",
    assignedTo: "",
  });

  useEffect(() => {
    fetchData({ start: startDate, end: endDate, page, limit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCell]);

  const fetchData = async (options = {}) => {
    try {
      setLoading(true);
      const rangeStart = options?.start || startDate;
      const rangeEnd = options?.end || endDate;
      const pageToUse = parseInt(options?.page || page || 1, 10);
      const limitToUse = parseInt(options?.limit || limit || 20, 10);

      const [merchantsRes, usersRes] = await Promise.all([
        merchantsApi.list({ limit: limitToUse, page: pageToUse }),
        usersApi.list({ limit: 100, status: "approved" }),
      ]);

      const merchantsList = merchantsRes.data?.merchants || [];
      const pagination = merchantsRes.data?.pagination || {};
      setTotalMerchants(pagination.total || merchantsList.length);
      setTotalPages(pagination.pages || 1);
      setPage(pagination.page || pageToUse);

      // Fetch transactions for each merchant
      const merchantsWithTransactions = await Promise.all(
        merchantsList.map(async (merchant) => {
          try {
            const txnRes = await merchantsApi.getTransactions(merchant._id, {
              limit: 1000,
              startDate: rangeStart,
              endDate: rangeEnd,
            });

            const transactions = txnRes.data?.transactions || [];
            const transactionMap = {};

            transactions.forEach((txn) => {
              const date = new Date(txn.transactionDate)
                .toISOString()
                .split("T")[0];
              transactionMap[date] = {
                count: txn.transactionCount,
                notes: txn.notes,
                id: txn._id,
              };
            });

            return {
              ...merchant,
              transactions: transactionMap,
            };
          } catch (error) {
            console.error(
              `Failed to fetch transactions for merchant ${merchant._id}:`,
              error
            );
            return {
              ...merchant,
              transactions: {},
            };
          }
        })
      );

      setMerchants(merchantsWithTransactions);
      setUsers(usersRes.data?.users || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load merchant data");
    } finally {
      setLoading(false);
    }
  };

  const handleCellEdit = (merchantId, field, date = null) => {
    const merchant = merchants.find((m) => m._id === merchantId);
    if (!merchant) return;

    let currentValue = "";

    if (date) {
      // Transaction count for specific date
      currentValue = merchant.transactions[date]?.count?.toString() || "0";
    } else if (field === "businessName") {
      currentValue = merchant.businessName || "";
    } else if (field === "remarks") {
      currentValue = merchant.remarks || "";
    } else if (field === "assignedTo") {
      currentValue = merchant.assignedTo || "";
    }

    setEditingCell({ merchantId, field, date });
    setEditValue(currentValue);
  };

  const handleCellSave = async () => {
    if (!editingCell) return;

    const { merchantId, field, date } = editingCell;
    const changeKey = `${merchantId}-${field}-${date || "field"}`;

    try {
      if (date) {
        // Save transaction data
        const count = parseInt(editValue) || 0;
        await merchantsApi.addTransaction(merchantId, {
          txn_date: date,
          txn_count: count,
          notes:
            merchants.find((m) => m._id === merchantId)?.transactions[date]
              ?.notes || "",
        });

        // Update local state
        setMerchants((prev) =>
          prev.map((merchant) => {
            if (merchant._id === merchantId) {
              return {
                ...merchant,
                transactions: {
                  ...merchant.transactions,
                  [date]: {
                    ...merchant.transactions[date],
                    count: count,
                  },
                },
              };
            }
            return merchant;
          })
        );
      } else {
        // Save merchant field
        const updateData = { [field]: editValue };
        await merchantsApi.update(merchantId, updateData);

        // Update local state
        setMerchants((prev) =>
          prev.map((merchant) => {
            if (merchant._id === merchantId) {
              return { ...merchant, [field]: editValue };
            }
            return merchant;
          })
        );
      }

      // Remove from pending changes
      setPendingChanges((prev) => {
        const newChanges = new Map(prev);
        newChanges.delete(changeKey);
        return newChanges;
      });

      toast.success("Changes saved successfully");
    } catch (error) {
      console.error("Failed to save changes:", error);
      toast.error("Failed to save changes");
    }

    setEditingCell(null);
    setEditValue("");
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditValue("");
  };

  const handleAddMerchant = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        username: newMerchant.username.trim(),
        businessName: newMerchant.businessName.trim(),
      };

      if (newMerchant.assignedTo) {
        payload.assignedTo = newMerchant.assignedTo;
      }

      await merchantsApi.create(payload);
      toast.success("Merchant added successfully");
      setShowAddModal(false);
      setNewMerchant({ username: "", businessName: "", assignedTo: "" });
      await fetchData({ start: startDate, end: endDate });
    } catch (error) {
      console.error("Failed to add merchant:", error);
      toast.error("Failed to add merchant");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMerchant = async () => {
    if (!merchantToDelete) return;

    try {
      await merchantsApi.delete(merchantToDelete._id);
      toast.success("Merchant deleted successfully");
      setShowDeleteModal(false);
      setMerchantToDelete(null);
      await fetchData({ start: startDate, end: endDate });
    } catch (error) {
      console.error("Failed to delete merchant:", error);
      toast.error("Failed to delete merchant");
    }
  };

  const handleScroll = (direction) => {
    if (!scrollContainerRef.current) return;

    const scrollAmount = 200;
    const newPosition =
      direction === "left"
        ? Math.max(0, scrollPosition - scrollAmount)
        : scrollPosition + scrollAmount;

    scrollContainerRef.current.scrollTo({
      left: newPosition,
      behavior: "smooth",
    });
    setScrollPosition(newPosition);
  };

  // Pagination helper
  const goToPage = (newPage) => {
    const p = Math.max(1, Math.min(newPage, totalPages || 1));
    if (p === page) return;
    setPage(p);
    fetchData({ start: startDate, end: endDate, page: p, limit });
  };

  const renderEditableCell = (merchantId, field, value, date = null) => {
    const isEditing =
      editingCell?.merchantId === merchantId &&
      editingCell?.field === field &&
      editingCell?.date === date;

    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <input
            ref={editInputRef}
            type={date ? "number" : "text"}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCellSave();
              if (e.key === "Escape") handleCellCancel();
            }}
            className="w-full px-2 py-1 text-xs border border-primary rounded focus:outline-none focus:ring-1 focus:ring-primary"
            min={date ? 0 : undefined}
          />
          <button
            onClick={handleCellSave}
            className="p-1 text-green-600 hover:bg-green-50 rounded"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            onClick={handleCellCancel}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      );
    }

    return (
      <div
        onClick={() => handleCellEdit(merchantId, field, date)}
        className="group cursor-pointer px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[28px] flex items-center"
      >
        <span className="text-xs text-gray-900 dark:text-gray-100 truncate">
          {value || (date ? "0" : "-")}
        </span>
        <Edit3 className="h-3 w-3 text-gray-400 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading merchant records...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-medium text-gray-900 dark:text-white font-lota">
            Manage merchant information and daily transaction records
          </h2>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
              <Calendar className="h-4 w-4 text-gray-500" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="text-xs bg-transparent focus:outline-none"
              />
              <span className="text-xs text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="text-xs bg-transparent focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  const t = new Date();
                  const s = new Date();
                  s.setDate(t.getDate() - 6);
                  setStartDate(toIsoDate(s));
                  setEndDate(toIsoDate(t));
                }}
              >
                Last 7d
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  const t = new Date();
                  const s = new Date();
                  s.setDate(t.getDate() - 29);
                  setStartDate(toIsoDate(s));
                  setEndDate(toIsoDate(t));
                }}
              >
                Last 30d
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  const t = new Date();
                  const s = new Date();
                  s.setDate(t.getDate() - 89);
                  setStartDate(toIsoDate(s));
                  setEndDate(toIsoDate(t));
                }}
              >
                90d
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  const t = new Date();
                  const s = new Date(t.getFullYear(), t.getMonth(), 1);
                  setStartDate(toIsoDate(s));
                  setEndDate(toIsoDate(t));
                }}
              >
                This Mo
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!startDate || !endDate)
                  return toast.error("Select a valid date range");
                if (new Date(startDate) > new Date(endDate))
                  return toast.error("Start date must be before end date");
                fetchData({ start: startDate, end: endDate });
              }}
              icon={RefreshCw}
            >
              Apply
            </Button>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={Plus}
          >
            Add Merchant
          </Button>
          {/* CSV Download Buttons */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadCSV(false)}
            style={{ minWidth: 120 }}
          >
            Download CSV
          </Button>
        </div>
      </div>

      {/* Spreadsheet Container */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Unified Scroll Container (Header + Body) */}
        <div ref={bodyScrollRef} className="max-h-[600px] overflow-y-auto">
          {/* Horizontal scroll wrapper shared by header and rows */}
          <div
            ref={scrollContainerRef}
            className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600"
            onScroll={(e) => setScrollPosition(e.target.scrollLeft)}
          >
            {/* Table Header */}
            <div className="relative border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 top-0 z-30">
              {/* Scroll Controls */}
              <div className="absolute right-2 top-2 z-40 flex gap-1">
                <button
                  onClick={() => handleScroll("left")}
                  className="p-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <ChevronLeft className="h-3 w-3 text-gray-600 dark:text-gray-300" />
                </button>
                <button
                  onClick={() => handleScroll("right")}
                  className="p-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <ChevronRight className="h-3 w-3 text-gray-600 dark:text-gray-300" />
                </button>
              </div>

              <div className="flex min-w-max relative">
                {/* Sticky S/N */}
                <div className="w-16 px-3 py-4 text-xs font-semibold text-gray-700 dark:text-gray-300 text-center border-r border-gray-200 dark:border-gray-700 sticky left-0 z-40 bg-gray-50 dark:bg-gray-900/50">
                  S/N
                </div>
                {/* Sticky Merchant */}
                <div className="w-48 px-4 py-4 text-xs font-semibold text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 sticky left-16 z-40 bg-gray-50 dark:bg-gray-900/50">
                  Merchant
                </div>
                {/* Date Columns */}
                {dateColumns.map((dateCol) => (
                  <div
                    key={dateCol.key}
                    className="w-28 px-2 py-4 text-xs font-semibold text-gray-700 dark:text-gray-300 text-center border-r border-gray-200 dark:border-gray-700"
                  >
                    <div>{dateCol.label}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                      {dateCol.fullDate.getFullYear()}
                    </div>
                  </div>
                ))}

                {/* Remarks Column */}
                <div className="w-32 px-3 py-4 text-xs font-semibold text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                  Remarks
                </div>

                {/* Assigned Merchant Column (Admin only) */}
                {userRole === "admin" && (
                  <div className="w-40 px-3 py-4 text-xs font-semibold text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                    Assigned To
                  </div>
                )}

                {/* Actions Column */}
                <div className="w-24 px-3 py-4 text-xs font-semibold text-gray-700 dark:text-gray-300 text-center">
                  Actions
                </div>
              </div>
            </div>

            {/* Rows wrapper */}
            <div className="relative">
              {merchants.length === 0 ? (
                <div className="p-12 text-center">
                  <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No merchants found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Add your first merchant to start tracking transactions
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => setShowAddModal(true)}
                    icon={Plus}
                  >
                    Add First Merchant
                  </Button>
                </div>
              ) : (
                <AnimatePresence>
                  {merchants.map((merchant, index) => (
                    <motion.div
                      key={merchant._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                      className={`border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                        index % 2 === 0
                          ? "bg-white dark:bg-gray-800"
                          : "bg-gray-50/50 dark:bg-gray-900/30"
                      }`}
                    >
                      <div className="flex min-w-max">
                        {/* Sticky S/N */}
                        <div className="w-16 px-3 py-4 text-xs text-gray-900 dark:text-gray-100 text-center border-r border-gray-200 dark:border-gray-700 flex items-center justify-center sticky left-0 z-20 bg-white dark:bg-gray-800">
                          {(page - 1) * limit + index + 1}
                        </div>
                        {/* Sticky Merchant */}
                        <div className="w-48 px-4 py-4 border-r border-gray-200 dark:border-gray-700 sticky left-16 z-20 bg-white dark:bg-gray-800">
                          <div className="space-y-1">
                            <div className="font-medium text-sm text-gray-900 dark:text-white">
                              @{merchant.username}
                            </div>
                            {renderEditableCell(
                              merchant._id,
                              "businessName",
                              merchant.businessName
                            )}
                            <div className="flex items-center gap-1">
                              <Badge
                                variant={
                                  merchant.status === "active"
                                    ? "success"
                                    : "danger"
                                }
                                size="sm"
                              >
                                {merchant.status}
                              </Badge>
                              {merchant.status === "flagged" && (
                                <span className="text-[10px] text-red-600 dark:text-red-400">
                                  ⚠️ Flagged
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Date Columns */}
                        {dateColumns.map((dateCol) => {
                          const transaction =
                            merchant.transactions[dateCol.key];
                          const count = transaction?.count || 0;
                          const isLowCount = count < 10;

                          return (
                            <div
                              key={dateCol.key}
                              className={`w-28 px-2 py-4 text-center border-r border-gray-200 dark:border-gray-700 ${
                                isLowCount ? "bg-red-50 dark:bg-red-900/20" : ""
                              }`}
                            >
                              {renderEditableCell(
                                merchant._id,
                                "transaction",
                                count,
                                dateCol.key
                              )}
                              {isLowCount && count > 0 && (
                                <div className="text-[10px] text-red-600 dark:text-red-400 mt-1">
                                  Low
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Remarks Column */}
                        <div className="w-32 px-3 py-4 border-r border-gray-200 dark:border-gray-700">
                          {renderEditableCell(
                            merchant._id,
                            "remarks",
                            merchant.remarks
                          )}
                        </div>

                        {/* Assigned Merchant Column (Admin only) */}
                        {userRole === "admin" && (
                          <div className="w-40 px-3 py-4 border-r border-gray-200 dark:border-gray-700">
                            <select
                              value={merchant.assignedTo || ""}
                              onChange={async (e) => {
                                try {
                                  await merchantsApi.update(merchant._id, {
                                    assignedTo: e.target.value,
                                  });
                                  setMerchants((prev) =>
                                    prev.map((m) =>
                                      m._id === merchant._id
                                        ? { ...m, assignedTo: e.target.value }
                                        : m
                                    )
                                  );
                                  toast.success("Assignment updated");
                                } catch (error) {
                                  toast.error("Failed to update assignment");
                                }
                              }}
                              className="w-full text-xs border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                            >
                              <option value="">Unassigned</option>
                              {users
                                .filter((u) =>
                                  ["staff", "admin"].includes(u.role)
                                )
                                .map((user) => (
                                  <option key={user._id} value={user._id}>
                                    {user.fullName}
                                  </option>
                                ))}
                            </select>
                          </div>
                        )}

                        {/* Actions Column */}
                        <div className="w-24 px-3 py-4 text-center">
                          <button
                            onClick={() => {
                              setMerchantToDelete(merchant);
                              setShowDeleteModal(true);
                            }}
                            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Delete merchant"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>
        {/* Footer with summary */}
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-4">
              <span>Total Merchants: {totalMerchants}</span>
              <span>
                Active: {merchants.filter((m) => m.status === "active").length}
              </span>
              <span>
                Flagged:{" "}
                {merchants.filter((m) => m.status === "flagged").length}
              </span>
              {/* Pagination controls */}
              <div className="ml-4 flex items-center gap-2">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded disabled:opacity-50"
                >
                  Prev
                </button>
                <div className="text-xs">
                  Page {page} / {totalPages}
                </div>
                <select
                  value={limit}
                  onChange={(e) => {
                    const newLimit = parseInt(e.target.value, 10);
                    setLimit(newLimit);
                    // reload first page with new limit
                    fetchData({
                      start: startDate,
                      end: endDate,
                      page: 1,
                      limit: newLimit,
                    });
                  }}
                  className="ml-2 text-xs px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded"
                >
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                  className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded"></div>
              <span className="text-xs">Low transaction days (&lt;10)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Add Merchant Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Merchant"
        size="md"
      >
        <form onSubmit={handleAddMerchant} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Username *
            </label>
            <input
              type="text"
              value={newMerchant.username}
              onChange={(e) =>
                setNewMerchant({ ...newMerchant, username: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter merchant username"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Business Name
            </label>
            <input
              type="text"
              value={newMerchant.businessName}
              onChange={(e) =>
                setNewMerchant({ ...newMerchant, businessName: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter business name"
            />
          </div>

          {userRole === "admin" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Assign To
              </label>
              <select
                value={newMerchant.assignedTo}
                onChange={(e) =>
                  setNewMerchant({ ...newMerchant, assignedTo: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Unassigned</option>
                {users
                  .filter((u) => ["staff", "admin"].includes(u.role))
                  .map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.fullName} ({user.username})
                    </option>
                  ))}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" loading={saving} icon={Plus}>
              Add Merchant
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Merchant"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete merchant{" "}
            <span className="font-semibold">@{merchantToDelete?.username}</span>
            ? This will remove all transaction records and cannot be undone.
          </p>

          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium text-red-800 dark:text-red-200">
                This action cannot be undone
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteMerchant}
              icon={Trash2}
            >
              Delete Merchant
            </Button>
          </div>
        </div>
      </Modal>

      {/* Keyboard Shortcuts Help */}
      <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
        💡 Click any cell to edit • Press Enter to save • Press Escape to cancel
      </div>
    </div>
  );
};

export default MerchantSpreadsheet;
